import React from "react";
import { MarketAnalysisMetrics } from "@/types/price-index";
import { ChevronUp, ChevronDown } from "@/components/bnp-icons";

interface MarketAnalysisCardsProps {
  metrics: MarketAnalysisMetrics;
  isLoading?: boolean;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  trend?: "up" | "down" | "neutral";
  icon?: React.ReactNode;
  suffix?: string;
  prefix?: string;
  className?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  trend,
  icon,
  suffix = "",
  prefix = "",
  className = "",
}) => {
  const getTrendColor = () => {
    if (trend === "up") return "text-green-primary";
    if (trend === "down") return "text-red-base";
    return "text-grey-4";
  };

  const getTrendBgColor = () => {
    if (trend === "up") return "bg-green-accent";
    if (trend === "down") return "bg-red-lighter";
    return "bg-grey-6";
  };

  return (
    <div
      className={`bg-white rounded-xl border border-grey-3 p-5 hover:shadow-md transition-shadow ${className}`}
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-medium text-grey-4">{title}</h3>
        {icon && <div className="text-bnp-cyan">{icon}</div>}
      </div>
      <div className="flex items-end justify-between">
        <div>
          <p className="text-2xl font-bold text-main-900">
            {prefix}
            {typeof value === "number" ? value.toLocaleString() : value}
            {suffix}
          </p>
          {change !== undefined && (
            <div className={`flex items-center gap-1 mt-2 ${getTrendColor()}`}>
              {trend === "up" && (
                <div className="w-4 h-4 [&>svg]:w-full [&>svg]:h-full">
                  <ChevronUp />
                </div>
              )}
              {trend === "down" && (
                <div className="w-4 h-4 [&>svg]:w-full [&>svg]:h-full">
                  <ChevronDown />
                </div>
              )}
              <span className="text-sm font-medium">
                {Math.abs(change).toFixed(2)}%
              </span>
            </div>
          )}
        </div>
        {change !== undefined && (
          <div className={`px-2 py-1 rounded-md ${getTrendBgColor()}`}>
            <span className={`text-xs font-semibold ${getTrendColor()}`}>
              {change > 0 ? "+" : ""}
              {change.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-xl border border-grey-3 p-5 animate-pulse">
    <div className="h-4 bg-grey-6 rounded w-1/2 mb-3"></div>
    <div className="h-8 bg-grey-6 rounded w-3/4 mb-2"></div>
    <div className="h-4 bg-grey-6 rounded w-1/3"></div>
  </div>
);

const MarketAnalysisCards: React.FC<MarketAnalysisCardsProps> = ({
  metrics,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  const getTrend = (value: number): "up" | "down" | "neutral" => {
    if (value > 0) return "up";
    if (value < 0) return "down";
    return "neutral";
  };

  const formatCurrency = (value: number): string => {
    if (value >= 1000000000) {
      return `${(value / 1000000000).toFixed(2)}B`;
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(2)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}K`;
    }
    return value.toFixed(2);
  };

  const getMarketTrendLabel = (trend: string): string => {
    const labels: Record<string, string> = {
      rising: "Rising",
      falling: "Falling",
      stable: "Stable",
    };
    return labels[trend] || trend;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <MetricCard
        title="Average Growth"
        value={metrics.averageGrowth.toFixed(2)}
        suffix="%"
        change={metrics.averageGrowth}
        trend={getTrend(metrics.averageGrowth)}
        icon={
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
            />
          </svg>
        }
      />

      <MetricCard
        title="Market Trend"
        value={getMarketTrendLabel(metrics.marketTrend)}
        icon={
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        }
      />

      <MetricCard
        title="Quarterly Change"
        value={metrics.quarterlyChange.toFixed(2)}
        suffix="%"
        change={metrics.quarterlyChange}
        trend={getTrend(metrics.quarterlyChange)}
        icon={
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
      />

      <MetricCard
        title="Yearly Change"
        value={metrics.yearlyChange.toFixed(2)}
        suffix="%"
        change={metrics.yearlyChange}
        trend={getTrend(metrics.yearlyChange)}
        icon={
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        }
      />

      {metrics.topPerformingLocations &&
        metrics.topPerformingLocations.length > 0 && (
          <div className="col-span-1 sm:col-span-2 lg:col-span-4">
            <div className="bg-white rounded-xl border border-grey-3 p-5">
              <h3 className="text-sm font-medium text-grey-4 mb-4">
                Top Performing Locations
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {metrics.topPerformingLocations
                  .slice(0, 3)
                  .map((location, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-weak-100 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-bnp-teal flex items-center justify-center">
                          <span className="text-sm font-bold text-bnp-cyan">
                            {index + 1}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-main-900">
                          {location.location}
                        </span>
                      </div>
                      <span
                        className={`text-sm font-semibold ${
                          location.growth > 0
                            ? "text-green-primary"
                            : "text-red-base"
                        }`}
                      >
                        {location.growth > 0 ? "+" : ""}
                        {location.growth.toFixed(2)}%
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default MarketAnalysisCards;
